import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Table from '../../components/ui/Table';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Can from '../../auth/Can';
import { listLeadSources, createLeadSource, updateLeadSource, deleteLeadSource } from '../../api/masters';
import { pushToast } from '../../components/ui/toastBus';

function LeadSourceFormModal({ open, onClose, source }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(source?.name || '');
  const [isActive, setIsActive] = useState(source?.is_active ?? true);
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      source ? updateLeadSource(source.id, { name, is_active: isActive }) : createLeadSource({ name, is_active: isActive }),
    onSuccess: () => {
      pushToast({ type: 'success', message: source ? 'Source updated.' : 'Source created.' });
      queryClient.invalidateQueries({ queryKey: ['lead-sources'] });
      onClose();
    },
    onError: (err) => setError(err.response?.data?.errors?.name?.[0] || err.response?.data?.message || 'Could not save source.'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    mutation.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={source ? 'Edit lead source' : 'New lead source'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={mutation.isPending}>
            Save
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} error={error} />
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          Active (shown when registering a new lead)
        </label>
      </form>
    </Modal>
  );
}

export default function LeadMasters() {
  const queryClient = useQueryClient();
  const [formTarget, setFormTarget] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const sourcesQuery = useQuery({
    queryKey: ['lead-sources', { include_inactive: true }],
    queryFn: () => listLeadSources({ include_inactive: true }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteLeadSource,
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Source deleted.' });
      queryClient.invalidateQueries({ queryKey: ['lead-sources'] });
      setDeleteTarget(null);
    },
    onError: (err) => {
      pushToast({ type: 'error', message: err.response?.data?.errors?.name?.[0] || err.response?.data?.message || 'Could not delete source.' });
      setDeleteTarget(null);
    },
  });

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'is_active', label: 'Status', render: (row) => <Badge tone={row.is_active ? 'green' : 'slate'}>{row.is_active ? 'Active' : 'Inactive'}</Badge> },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className="flex items-center gap-3 text-sm">
          <Can perm="masters.manage">
            <button
              onClick={() => {
                setFormTarget(row);
                setFormOpen(true);
              }}
              className="text-primary hover:underline"
            >
              Edit
            </button>
            <button onClick={() => setDeleteTarget(row)} className="text-red-600 hover:underline">
              Delete
            </button>
          </Can>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Lead Masters"
        description="Manage the source options offered when registering a lead."
        actions={
          <Can perm="masters.manage">
            <Button
              onClick={() => {
                setFormTarget(null);
                setFormOpen(true);
              }}
            >
              New Source
            </Button>
          </Can>
        }
      />

      <Table columns={columns} data={sourcesQuery.data || []} isLoading={sourcesQuery.isLoading} emptyMessage="No lead sources yet." />

      {formOpen && <LeadSourceFormModal open={formOpen} onClose={() => setFormOpen(false)} source={formTarget} />}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
        title="Delete lead source"
        message={`Delete "${deleteTarget?.name}"? This is only possible if no lead references it.`}
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
